import DataGrid from '@components/common/DataGrid';
import ErrorPage from '@components/common/ErrorPage';
import Header from '@components/common/layout/Header';
import Notifier from '@components/common/Notifier';
import React, {useMemo} from 'react';
import SearchField from '@components/common/SearchField';
import routePath, {Routes} from '@account/utils/route';
import {Box, Link} from '@mui/material';
import {Link as RouterLink, useHistory} from 'react-router-dom';
import {customerAccountSearchParams} from '@account/utils/customer-account-search';
import {getError} from '@account/utils/errors';
import {hasRoleLevel, roleLevels} from '@account/utils';
import {plainDateStamp} from '@account/utils/dates';
import {useAuth} from '@apps-common/auth-react';
import {useCustomerAccounts} from '@hooks/data/ims/useCustomerAccounts';
import {useFlags} from '@hooks/useFlags';
import {useTableQueryParams} from '@hooks/query-params/useTableQueryParams';

const AccountsPage = () => {
  const auth = useAuth();
  const history = useHistory();

  const {showAllAccounts} = useFlags();

  const isPlanetAdmin = hasRoleLevel(auth, [roleLevels.planet_admin]);

  const {page, pageSize, search, handlePaginationModelChange} =
    useTableQueryParams();

  const {data, isFetching, isLoading, error} = useCustomerAccounts(
    {
      limit: pageSize,
      offset: page * pageSize,
      ...customerAccountSearchParams(search),
    },
    {
      keepPreviousData: true,
      enabled: showAllAccounts && isPlanetAdmin && pageSize > 0,
    }
  );

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Account Name',
        flex: 1.25,
        renderCell: params => (
          <Link
            component={RouterLink}
            to={routePath(Routes.ACCOUNT_DETAIL, {accountId: params.row.id})}
            variant="body2"
            onClick={e => e.stopPropagation()}
            underline="hover"
          >
            {params.value}
          </Link>
        ),
      },
      {
        field: 'id',
        headerName: 'ID',
        flex: 0.85,
      },
      {
        field: 'created_at',
        headerName: 'Created At',
        flex: 0.85,
        valueGetter: params => plainDateStamp(params.row?.created_at),
      },
      {
        field: 'sfdc_id',
        headerName: 'SFDC ID',
        flex: 0.75,
        valueGetter: params => params.value || '--',
      },
      {
        field: 'program_id',
        headerName: 'Program ID',
        flex: 0.5,
        valueGetter: params => params.value || '--',
      },
    ],
    []
  );

  const results = data?.results || [];

  const handleRowClick = params =>
    history.push(routePath(Routes.ACCOUNT_DETAIL, {accountId: params.id}));

  if (!showAllAccounts || !isPlanetAdmin) {
    return <ErrorPage statusCode={404} />;
  }

  if (error) {
    return <ErrorPage {...getError(error)} />;
  }

  return (
    <>
      <Header name="All Accounts" />

      <Box display="flex" justifyContent="space-between" mb={2} flexWrap="wrap">
        <SearchField placeholder="Search by name or account ID" />
      </Box>

      <DataGrid
        columns={columns}
        disableColumnSorting
        onPaginationModelChange={handlePaginationModelChange}
        onRowClick={handleRowClick}
        pagination
        paginationMode="server"
        paginationModel={{page, pageSize}}
        rowCount={results.length ? data?.meta?.count : 0}
        rows={results}
        slotProps={{
          noRowsOverlay: {message: 'No accounts'},
          noResultsOverlay: {message: 'No results'},
        }}
        loading={isLoading || isFetching}
      />

      <Notifier open={isLoading} message="Loading accounts..." progress />
    </>
  );
};

export default AccountsPage;
